/**
 * ObjectComponent — R3F component for rendering ObjectManager-managed assets.
 *
 * Reads states from ObjectManager and renders objects that should be visible
 * (status: visible, revealing, or hiding). Each managed object gets its own
 * \<group\> with auto-center transform (if configured).
 *
 * Responsibilities:
 * - Monitor ObjectManager state for which objects to render
 * - Clone and prepare GLTF scenes (shadows, anisotropy)
 * - Apply auto-center transform using computeModelTransform()
 * - Call objectManager.attachScene() once mounted in the R3F tree
 * - Call objectManager.tick() each frame for animations
 *
 * @see docs/ACTIVE_TASK.md — M5.2 Teil 5: ObjectComponent
 * @see packages/engine/src/core/ModelComponent.tsx — pattern reference
 */

import { useEffect, useRef, useMemo, useState, memo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Box3, Vector3 } from 'three';
import type { Group, Mesh, Material, Texture, Object3D, MeshStandardMaterial } from 'three';
import type { ObjectManager } from './ObjectManager';
import type { ManagedObjectState } from '../api/types/objectManager';
import type { RevealObject } from './RevealController';
import type { SwapGroup } from './SwapAnimator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ObjectComponentProps {
  /** The ObjectManager instance. */
  objectManager: ObjectManager;
  /**
   * Vertical offset (metres) to sink non-parentRelative objects below y=0,
   * matching the main model's groundSinkY. Default: 0
   */
  groundSinkY?: number;
  /**
   * Controls which objects this instance renders.
   *
   * - `'world'` (default): renders non-parentRelative objects at world level.
   *   These get their own autoCenter + groundSinkY applied.
   * - `'body'`: renders parentRelative objects only, with position [0,0,0].
   *   Must be rendered INSIDE the body model's group so transforms align.
   */
  scope?: 'world' | 'body';
}

/** Internal per-object render state. */
interface ObjectRenderEntry {
  id: string;
  clonedScene: Object3D;
  offset: Vector3;
  scale: number;
  parentRelative: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Prepare a cloned scene: enable shadows, anisotropic filtering. */
function prepareScene(scene: Object3D, maxAnisotropy: number): Object3D {
  const clone = scene.clone(true);

  clone.traverse((child) => {
    if (!(child as { isMesh?: boolean }).isMesh) return;

    const meshObj = child as unknown as Mesh;
    meshObj.castShadow = true;

    const mats: Material[] = Array.isArray(meshObj.material)
      ? meshObj.material
      : meshObj.material
        ? [meshObj.material]
        : [];

    for (const mat of mats) {
      const stdMat = mat as MeshStandardMaterial;

      if (stdMat.transparent) {
        stdMat.depthWrite = true;
        meshObj.renderOrder = 10;
      }

      const textureMaps: (Texture | null | undefined)[] = [
        stdMat.map,
        stdMat.normalMap,
        stdMat.roughnessMap,
        stdMat.metalnessMap,
        stdMat.aoMap,
        stdMat.emissiveMap,
      ];
      for (const tex of textureMaps) {
        if (tex && tex.anisotropy !== maxAnisotropy) {
          tex.anisotropy = maxAnisotropy;
          tex.needsUpdate = true;
        }
      }
    }
  });

  return clone;
}

/**
 * Compute auto-center transform for an Object3D.
 * Centers XZ at origin, bottom at Y=0.
 */
function computeAutoCenter(scene: Object3D): { offset: Vector3; scale: number } {
  const box = new Box3();

  if ('isObject3D' in scene && (scene as Object3D).isObject3D) {
    scene.updateWorldMatrix(true, true);
    box.setFromObject(scene);
  }

  if (box.isEmpty()) {
    return { offset: new Vector3(0, 0, 0), scale: 1 };
  }

  const center = new Vector3();
  box.getCenter(center);

  return {
    offset: new Vector3(-center.x, -box.min.y, -center.z),
    scale: 1,
  };
}

// ---------------------------------------------------------------------------
// Single managed object renderer
// ---------------------------------------------------------------------------

interface ManagedObjectRendererProps {
  entry: ObjectRenderEntry;
  objectManager: ObjectManager;
  groundSinkY: number;
}

const ManagedObjectRenderer = memo(function ManagedObjectRenderer({ entry, objectManager, groundSinkY }: ManagedObjectRendererProps) {
  const groupRef = useRef<Group>(null);
  const invalidate = useThree((s) => s.invalidate);

  // When mounted, notify ObjectManager with scene and group refs
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;

    objectManager.attachScene(
      entry.id,
      entry.clonedScene as unknown as RevealObject,
      group as unknown as SwapGroup,
    );

    invalidate();
  }, [entry.id, entry.clonedScene, objectManager, invalidate]);

  return (
    <group ref={groupRef} scale={entry.scale}>
      <primitive
        object={entry.clonedScene}
        position={[
          entry.offset.x,
          entry.offset.y - (entry.parentRelative ? 0 : groundSinkY),
          entry.offset.z,
        ]}
      />
    </group>
  );
});

// ---------------------------------------------------------------------------
// ObjectComponent
// ---------------------------------------------------------------------------

export function ObjectComponent({ objectManager, groundSinkY = 0, scope = 'world' }: ObjectComponentProps) {
  const [renderState, setRenderState] = useState<ManagedObjectState[]>([]);
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);

  // Prepared scenes cache (clone + prepare once per object)
  const preparedScenes = useRef(new Map<string, ObjectRenderEntry>());

  // Subscribe to ObjectManager state changes
  useEffect(() => {
    /** Previous visible IDs — avoids setState when nothing changed. */
    let prevIds = '';

    const update = () => {
      // Always request a render frame — even if the managed-object ID list
      // hasn't changed, base-node visibility toggles still need a redraw.
      invalidate();

      const state = objectManager.getState();
      // Only render objects that should be in the scene.
      // Base nodes are excluded — they stay in ModelComponent's scene graph
      // and ObjectManager controls their visibility directly.
      // Filter by scope: 'body' renders parentRelative objects (inside the body
      // group); 'world' renders non-parentRelative objects at scene root.
      const visible = state.objects.filter(
        (o) =>
          !o.isBaseNode &&
          (scope === 'body' ? o.parentRelative : !o.parentRelative) &&
          (o.status === 'visible' ||
          o.status === 'revealing' ||
          o.status === 'hiding'),
      );

      // Cheap ID comparison — skip setState when the same objects are visible
      const ids = visible.map((o) => o.id).join(',');
      if (ids === prevIds) return;
      prevIds = ids;

      setRenderState(visible);
    };

    const unsubscribe = objectManager.addChangeListener(update);

    // Initial state
    update();

    return unsubscribe;
  }, [objectManager, invalidate, scope]);

  // Ensure a render frame AFTER React commits tree changes.
  // Without this, the frame triggered by onChange's invalidate() fires BEFORE
  // React unmounts/mounts primitives (setState is async). In on-demand mode
  // this leaves stale geometry visible until the next external event (orbit).
  useEffect(() => {
    invalidate();
  }, [renderState, invalidate]);

  // Prepare render entries for visible objects
  const entries: ObjectRenderEntry[] = useMemo(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
    const result: ObjectRenderEntry[] = [];

    for (const obj of renderState) {
      // eslint-disable-next-line react-hooks/refs -- intentional: preparedScenes is a mutable scene cache, not render state
      let existing = preparedScenes.current.get(obj.id);
      if (!existing) {
        // Get the raw scene from ObjectManager
        const rawState = objectManager.getObjectState(obj.id);
        if (!rawState) continue;

        // Access the pristine loaded scene via typed accessor (loadedScene is never overwritten
        // by attachScene, so it has no R3F-applied transforms baked in).
        const internal = objectManager.getLoadedScene(obj.id);
        const internalScene = internal?.loadedScene;
        if (!internalScene) continue;

        // Clone scene (this is what gets rendered in R3F)
        const clonedScene = prepareScene(internalScene as Object3D, maxAnisotropy);

        const config = internal?.config;

        let offset: Vector3;
        let scale: number;

        if (scope === 'body' && config?.group) {
          // Body-scope: align external objects with the base node (OEM wheels).
          // The group's anchorCenter was computed from the base node in the
          // original gltf.scene (pristine, no R3F transforms). We compute
          // the external object's own bbox center and offset it to match.
          const anchor = objectManager.getGroupAnchor(config.group);
          if (anchor) {
            clonedScene.updateWorldMatrix(false, true);
            const extBox = new Box3();
            extBox.setFromObject(clonedScene);
            const extCenter = extBox.getCenter(new Vector3());
            offset = new Vector3(
              anchor.x - extCenter.x,
              anchor.y - extCenter.y,
              anchor.z - extCenter.z,
            );
          } else {
            offset = new Vector3(0, 0, 0);
          }
          scale = 1;
        } else if (scope === 'world') {
          // World-scope: autoCenter + groundSinkY
          const autoCenter = config?.transform?.autoCenter ?? true;
          const result = autoCenter
            ? computeAutoCenter(clonedScene)
            : { offset: new Vector3(0, 0, 0), scale: 1 };
          offset = result.offset;
          scale = result.scale;
        } else {
          offset = new Vector3(0, 0, 0);
          scale = 1;
        }

        existing = {
          id: obj.id,
          clonedScene,
          offset,
          scale,
          parentRelative: obj.parentRelative,
        };
        // eslint-disable-next-line react-hooks/refs -- intentional: mutable scene cache keyed by object id
        preparedScenes.current.set(obj.id, existing);
      }
      result.push(existing);
    }

    // Remove stale entries
    // eslint-disable-next-line react-hooks/refs -- intentional: mutable scene cache cleanup
    for (const [id] of preparedScenes.current) {
      if (!renderState.find((o) => o.id === id)) {
        // eslint-disable-next-line react-hooks/refs -- intentional: mutable scene cache cleanup
        preparedScenes.current.delete(id);
      }
    }

    return result;
  }, [renderState, objectManager, gl, scope]);

  // Per-frame animation tick
  useFrame((_, delta) => {
    objectManager.tick(delta * 1000); // delta is in seconds, tick expects ms
    // Request next frame while reveal/swap animations are running
    if (objectManager.isAnimating) invalidate();
  });

  return (
    <>
      {entries.map((entry) => (
        <ManagedObjectRenderer
          key={entry.id}
          entry={entry}
          objectManager={objectManager}
          groundSinkY={groundSinkY}
        />
      ))}
    </>
  );
}
